"use client";
import { track, useSectionView } from "@/app/utils/analytics";
import { motion } from "motion/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "What do I get exactly?",
    answer: (
      <>
        <p>With your purchase, you receive:</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>A complete React & WXT framework boilerplate</li>
          <li>Stripe payment processing for collecting payments</li>
          <li>Pre-configured authentication system (Email/Password, Google)</li>
          <li>Database and backend setup with Firebase</li>
          <li>Ready-to-use UI components and sections</li>
          <li>Detailed documentation and setup guides</li>
          <li>Access to future updates and improvements</li>
        </ul>
        <p className="mt-3">
          Essentially, everything you will need to start off your Chrome
          extension development and save dozens of precious hours!
        </p>
      </>
    ),
  },
  {
    question: "Can I get a refund?",
    answer: (
      <p>
        Yes, if you decided that you don't want ExtensionFast anymore within 30
        days of purchase, you can request a full 100% refund, no questions
        asked.
        <br />
        <br />
        Email us here: mmcgarvey@extensionfast.com
      </p>
    ),
  },
  {
    question: "Can I replace or customize the existing features?",
    answer: (
      <>
        <p>
          Absolutely! You can add/remove anything you need to, you own the code,
          and the codebase is designed with customization in mind.
        </p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>
            Clear separation of features that makes it easy to swap them out if
            needed
          </li>
          <li>Well-organized components that are easy to modify and replace</li>
        </ul>
      </>
    ),
  },
  {
    question: "How is ExtensionFast better than other boilerplates?",
    answer: (
      <>
        <p>
          Customers do NOT pay for lines of code.
          <br />
          Customers pay for results that improve their lives.
        </p>
        <ul className="list-disc ml-6 mt-3 space-y-1">
          <li>Documentation with video guides</li>
          <li>
            Emphasis on simplicity: Focuses on essentials, gets rid of bloat
            code
          </li>
          <li>
            Close support by the founder, you can reach out anytime if you need
            help
          </li>
          <li>
            Designed to help you code, monetize, and submit your extension to
            the Chrome Web Store.
          </li>
        </ul>
      </>
    ),
  },
  {
    question: "Does this work for other browsers?",
    answer: (
      <p>
        Yes! ExtensionFast is mainly targeted to those using Chrome as their
        browser, but you will be able to create browser extensions for both
        Firefox and Microsoft Edge as well.
      </p>
    ),
  },
  {
    question: "Are there any extra associated costs?",
    answer: (
      <>
        <p>
          Regarding ExtensionFast, all of the services offer a generous free
          tier to get you started, so there is no worry there.
        </p>
        <ul className="list-disc ml-6 mt-3 space-y-1">
          <li>Chrome Web Store developer, one-time registration fee of $5</li>
        </ul>
      </>
    ),
  },
  {
    question: "I have another question",
    answer: (
      <p>
        I'm here to help! If you have any other questions, feel free to reach
        out to our email:
        <br />
        <span className="font-medium text-zinc-200">
          mmcgarvey@extensionfast.com
        </span>
        <br />
        <br />
        We'll respond within the same day if not a few hours.
      </p>
    ),
  },
];

export default function FAQ() {
  const sectionRef = useSectionView("faq");
  return (
    <section ref={sectionRef}>
      <div
        className="bg-ink flex flex-col justify-center items-center w-full py-24 px-6"
        id="faq"
      >
        {/* Section header */}
        <div className="w-full xl:w-[1250px] mx-auto text-center items-center flex flex-col">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-panel/70 px-4 py-1.5 text-sm font-medium text-zinc-300 shadow-sm backdrop-blur">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
            >
              <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
            Questions & Answers
          </div>
          <h2 className="text-4xl md:text-5xl font-medium text-white tracking-tight text-center max-w-3xl mx-auto">
            Frequently asked questions
          </h2>
          <p className="text-lg text-zinc-400 mt-4">
            Everything you need to know about ExtensionFast. Can't find the
            answer? Reach out to us.
          </p>
        </div>

        {/* Accordion */}
        <motion.div className="w-full xl:w-[1215px] mx-auto mt-12">
          <Accordion
            className="w-full flex flex-col gap-y-3"
            type="single"
            collapsible
            onValueChange={(value) => {
              if (value)
                track("faq_opened", {
                  question: faqs[Number(value.replace("item-", ""))]?.question,
                });
            }}
          >
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                className="bg-panel border border-line rounded-2xl px-6 transition-all duration-200 hover:bg-panel-light hover:border-zinc-700 data-[state=open]:bg-panel data-[state=open]:border-primary/40 data-[state=open]:shadow-lg data-[state=open]:shadow-black/30"
                value={`item-${index}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold text-white hover:no-underline py-5 [&[data-state=open]]:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-base text-zinc-400 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
